import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react'
import StatusDot from '../IssueStatuses/StatusDot'
import StatusCategoryHeading from './StatusCategoryHeading'
import {
  STATUS_GROUP_CLASS, STATUS_LIST_CLASS, groupByCategory, statusOptionClassName
} from './statusOptions'
import useInlineFloatingMenu from '../../../hooks/useInlineFloatingMenu'

const POPOVER_CLASS = 'tw:z-[1100] tw:w-72 tw:max-h-[335px] tw:overflow-y-auto tw:bg-white tw:rounded! '
  + `tw:shadow-[0_0_7px_0_rgba(0,0,0,0.1)]! ${STATUS_LIST_CLASS}`

const StatusInlineEditor = ({
  anchorEl = null, issueStatuses, currentStatusId = null, isOpen, onClose, onSelect
}) => {
  const { refs, floatingStyles, context, getFloatingProps } = useInlineFloatingMenu({
    anchorEl, isOpen, onClose
  })

  const handleOptionClick = useCallback(event => {
    const { value } = event.currentTarget.dataset
    if (!value)
      return
    onSelect(value)
    onClose()
  }, [onSelect, onClose])

  if (!isOpen || !anchorEl)
    return null

  const renderOption = status => {
    const isSelected = String(status.id) === String(currentStatusId)
    return (
      <button
        key={status.id}
        aria-selected={isSelected}
        className={statusOptionClassName({ isFocused: false, isSelected })}
        data-value={status.id}
        role="option"
        type="button"
        onClick={handleOptionClick}
      >
        <StatusDot color={status.color} label={status.name} />
      </button>
    )
  }

  const renderGroup = group => (
    <div key={group.value} className={STATUS_GROUP_CLASS} role="group">
      <StatusCategoryHeading data={group} />
      {group.options.map(renderOption)}
    </div>
  )

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} initialFocus={-1} modal={false}>
        <div
          ref={refs.setFloating}
          aria-label="Modifier le statut"
          className={POPOVER_CLASS}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {groupByCategory(issueStatuses).map(renderGroup)}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  )
}

StatusInlineEditor.propTypes = {
  anchorEl: PropTypes.instanceOf(Element),
  currentStatusId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isOpen: PropTypes.bool.isRequired,
  issueStatuses: PropTypes.arrayOf(PropTypes.shape({
    category: PropTypes.string,
    color: PropTypes.string,
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  })),
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
}

StatusInlineEditor.defaultProps = {
  anchorEl: null,
  currentStatusId: null,
  issueStatuses: []
}

export default StatusInlineEditor
